// Gravação das reuniões do Escritório (0.45). Só o administrador do escritório grava; o LiveKit grava a sala
// e o arquivo vai para os Documentos do escritório. Todos na reunião veem o aviso "Gravando".
// Vídeo sai em partes de 14 minutos (cada arquivo fica abaixo do limite de 50 MB do plano gratuito).
import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';
import { DEMO } from './env';
import { t } from './i18n';

type Resp = { egress: string; doc: string; parteMin: number };
async function chamar(corpo: Record<string, unknown>): Promise<Resp> {
  if (DEMO || !supabase) throw new Error(t('Na prévia de demonstração não há gravação de verdade.'));
  const { data, error } = await supabase.functions.invoke('chamada', { body: corpo });
  if (error) {
    let msg = String(error.message ?? error);
    try { const c = await (error as any).context?.json?.(); if (c?.erro) msg = c.erro; } catch { /* segue */ }
    throw new Error(msg);
  }
  return data as Resp;
}

export async function podeGravar(conversa: string): Promise<boolean> {
  if (DEMO || !supabase) return false;
  const { data, error } = await supabase.rpc('escritorio_pode_gravar', { p_conversa: conversa });
  return !error && !!(data as any)?.grupo;
}

/** room: a sala do LiveKit (livekit-client Room). Devolve o estado e as ações da gravação. */
export function useGravacao(conversa: string, room: any) {
  const [pode, setPode] = useState(false);
  const [gravandoSala, setGravandoSala] = useState(!!room?.isRecording);
  const [minha, setMinha] = useState<{ egress: string; midia: 'audio' | 'video'; parte: number; desde: number } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const ref = useRef(minha);
  ref.current = minha;

  useEffect(() => { podeGravar(conversa).then(setPode, () => setPode(false)); }, [conversa]);
  useEffect(() => {
    if (!room) return;
    const f = (on: boolean) => setGravandoSala(!!on);
    setGravandoSala(!!room.isRecording);
    room.on?.('recordingStatusChanged', f);
    return () => { room.off?.('recordingStatusChanged', f); };
  }, [room]);
  // vídeo: troca de parte a cada 14 minutos
  useEffect(() => {
    if (!minha || minha.midia !== 'video') return;
    const i = setTimeout(async () => {
      const m = ref.current;
      if (!m) return;
      try {
        const r = await chamar({ acao: 'continuar', conversa, egress: m.egress, parte: m.parte + 1 });
        setMinha({ egress: r.egress, midia: 'video', parte: m.parte + 1, desde: Date.now() });
      } catch (e) { setErro(String((e as Error)?.message ?? e)); setMinha(null); }
    }, 14 * 60 * 1000);
    return () => clearTimeout(i);
  }, [minha?.egress]); // eslint-disable-line react-hooks/exhaustive-deps
  // saiu da reunião gravando: para a gravação
  useEffect(() => () => { const m = ref.current; if (m) chamar({ acao: 'parar', egress: m.egress }).catch(() => {}); }, []);

  const iniciar = async (midia: 'audio' | 'video') => {
    setOcupado(true); setErro('');
    try { const r = await chamar({ acao: 'gravar', conversa, midia }); setMinha({ egress: r.egress, midia, parte: 1, desde: Date.now() }); }
    catch (e) { setErro(String((e as Error)?.message ?? e)); }
    finally { setOcupado(false); }
  };
  const parar = async () => {
    const m = ref.current;
    if (!m) return;
    setOcupado(true);
    try { await chamar({ acao: 'parar', egress: m.egress }); } catch (e) { setErro(String((e as Error)?.message ?? e)); }
    setMinha(null); setOcupado(false);
  };
  return { pode, gravando: gravandoSala || !!minha, minha, ocupado, erro, iniciar, parar, limparErro: () => setErro('') };
}
